import { CartAddEvent } from '@theme/events';

/**
 * A lightweight, native (no third-party app) "cart upsell" carousel.
 * Renders a horizontal row of suggested products inside the cart drawer,
 * with a variant selector (when needed) and a one-click add button.
 */
class CartUpsellComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleVariantChange);
  }

  #handleVariantChange = (event) => {
    const select = event.target.closest('[data-upsell-variant-select]');
    if (!select) return;
    const button = select.closest('[data-upsell-item]')?.querySelector('[data-upsell-add]');
    if (button) button.dataset.variantId = select.value;
  };

  #handleClick = (event) => {
    const button = event.target.closest('[data-upsell-add]');
    if (!button) return;
    event.preventDefault();
    this.#addToCart(button);
  };

  /**
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const defaultLabel = button.dataset.labelDefault || button.textContent.trim();
    const addedLabel = button.dataset.labelAdded || defaultLabel;

    button.disabled = true;
    button.textContent = button.dataset.labelLoading || defaultLabel;

    try {
      const cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.description || data?.message || 'cart add failed');
      }

      button.textContent = addedLabel;

      // Let the rest of the theme (cart drawer, cart icon bubble, cart items list)
      // know the cart changed. cart-items-component listens for this and re-renders
      // itself natively (no section payload needed - it falls back to a fresh fetch).
      document.dispatchEvent(new CartAddEvent(data, this.id || null, {}));

      window.setTimeout(() => {
        button.textContent = defaultLabel;
        button.disabled = false;
      }, 1800);
    } catch (error) {
      console.error(error);
      button.textContent = button.dataset.labelError || 'Erreur';
      window.setTimeout(() => {
        button.textContent = defaultLabel;
        button.disabled = false;
      }, 1800);
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
